import React, { memo } from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { CheckCircleOutlined, CloseCircleOutlined, FileExcelOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import styles from "../ImportReport.module.css"

/**
 * Import Summary Component
 * Displays file information and import statistics
 * Follows Single Responsibility Principle - only handles summary display
 * Memoized to prevent unnecessary re-renders
 *
 * @param {Object} props - Component props
 * @param {string} props.filename - Name of the imported file
 * @param {number} props.successCount - Number of successfully imported clients
 * @param {number} props.failedCount - Number of failed imports
 */
const ImportSummary = memo(({ filename, successCount, failedCount }) => {
  const { t } = useTranslation()

  return (
    <>
      {/* File Header */}
      <Flex justify="space-between" wrap align="center" className={styles.fileHeader}>
        <div className={styles.fileInfo}>
          <FileExcelOutlined className={styles.fileIcon} />
          <span>{filename}</span>
        </div>
        <span className={styles.fileStatus}>{t("clients.import_report.completed_with_errors")}</span>
      </Flex>

      {/* Import Summary */}
      <Flex gap={20} wrap className={styles.importSummary}>
        <span className={styles.success}>
          <CheckCircleOutlined className={styles.successIcon} />{" "}
          {t("clients.import_report.clients_imported_success", { count: successCount })}
        </span>
        <span className={styles.error}>
          <CloseCircleOutlined className={styles.errorIcon} /> {t("clients.import_report.clients_failed", { count: failedCount })}
        </span>
      </Flex>

      <p className={styles.infoText}>{t("clients.import_report.review_results")}</p>
    </>
  )
})

ImportSummary.displayName = "ImportSummary"

ImportSummary.propTypes = {
  filename: PropTypes.string.isRequired,
  successCount: PropTypes.number.isRequired,
  failedCount: PropTypes.number.isRequired
}

export default ImportSummary
